import React, { useState } from 'react'
import { useLazyQuery } from '@apollo/client'
import { Button } from '@resiliatech/birdsong'
import moment from 'moment'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  Link,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { DASHBOARD_IMPACT_REPORT } from 'Apollo/Queries/DashboardImpactReport'
import {
  CategoryActivity,
  GoalsPrioritizedByTaskCreateDate,
  FunderCollaboratedGoal,
  TasksSummary,
} from 'Generated/graphql-types'
import { SuperLoading } from 'Components/SharedComponents/Loading/SuperLoading'
import { ReportContainer, Section } from './ImpactReport.styled'

export const ImpactReport = () => {
  const {
    nonprofitOrgConfig: { orgId: userNonprofitOrgId },
  } = useAppState()

  const [nonprofitOrgIdToSearch, setNonprofitOrgIdToSearch] = useState('')
  const [searchType, setSearchType] = useState<'search' | 'user'>('search')

  const [getImpactReport, { error, loading, data }] = useLazyQuery(
    DASHBOARD_IMPACT_REPORT
  )

  const {
    dashboardImpactReport: {
      reportStartDate = '',
      reportEndDate = '',
      funderCollaboratedGoals = [],
      goalsPrioritizedByTaskCreateDate = [],
      impactGoalsUrl = '',
      tasksSummary = {} as TasksSummary,
      pastReportPeriodActivity = [],
      showEmptyState = false,
    } = {},
  } = data || {}

  const handleSearch = (type: 'search' | 'user') => {
    const orgIdToSearch =
      type === 'search' ? Number(nonprofitOrgIdToSearch) : userNonprofitOrgId
    setSearchType(type)

    getImpactReport({
      variables: { nonprofitOrgId: orgIdToSearch },
      fetchPolicy: 'network-only',
    })
  }

  if (loading) return <SuperLoading />

  const pastReportPeriodActivityEntries = pastReportPeriodActivity.map(
    (entry: CategoryActivity) => (
      <TableRow key={entry.goalCategory}>
        <TableCell>{entry.goalCategory}</TableCell>
        <TableCell>{entry.goalsCreated}</TableCell>
        <TableCell>{entry.tasksCompleted}</TableCell>
        <TableCell>{entry.academyResourcesViewed}</TableCell>
      </TableRow>
    )
  )

  const emptyStateMessage = `No goals found for nonprofitOrgId: ${nonprofitOrgIdToSearch}`

  return (
    <ReportContainer>
      <Section>
        <Box display='flex' gap={5} height='56px'>
          <TextField
            required
            label='Nonprofit Org ID'
            value={nonprofitOrgIdToSearch}
            onChange={({ target: { value } }) =>
              setNonprofitOrgIdToSearch(value)
            }
          />
          <Button
            isDisabled={!nonprofitOrgIdToSearch}
            label='Search Nonprofit Org ID'
            onClick={() =>
              nonprofitOrgIdToSearch ? handleSearch('search') : undefined
            }
            type='button'
            variant='secondary'
          />
          <Button
            label="Load User's Nonprofit Org ID"
            onClick={() =>
              userNonprofitOrgId ? handleSearch('user') : undefined
            }
            type='button'
            variant='secondary'
          />
        </Box>
      </Section>
      {error && (
        <Section>
          <Typography color='error'>{error.message}</Typography>
        </Section>
      )}
      {showEmptyState && (
        <Section>
          <Typography>{emptyStateMessage}</Typography>
        </Section>
      )}
      {data && !showEmptyState && (
        <>
          <Section>
            <Typography>
              You are viewing the Impact Report preview for{' '}
              {searchType === 'user' && `user's`} nonprofit org ID:{' '}
              {searchType === 'search'
                ? nonprofitOrgIdToSearch
                : userNonprofitOrgId}
            </Typography>
          </Section>
          <Section>
            <Typography variant='h5'>
              Impact Report for {reportStartDate} to {reportEndDate}
            </Typography>
          </Section>
          <Section>
            <Typography variant='h5'>
              Section 1: Your team completed{' '}
              {tasksSummary.totalTasksCompletedThisReportPeriod} tasks during
              the last report period
            </Typography>
          </Section>
          <Section>
            <Typography variant='h5'>
              Section 2: Your team&apos;s activities during the last report
              period
            </Typography>
            <Paper>
              <TableContainer sx={{ marginTop: '20px' }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Goal Category</TableCell>
                      <TableCell>Goals Created</TableCell>
                      <TableCell>Tasks Completed</TableCell>
                      <TableCell>Academy Resources Viewed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{pastReportPeriodActivityEntries}</TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Section>
          <Section>
            <Typography variant='h5'>Section 3: Task Summary</Typography>
            <Paper>
              <TableContainer sx={{ marginTop: '20px' }}>
                <Table>
                  <TableBody>
                    <TableRow>
                      <TableCell>All Completed Tasks</TableCell>
                      <TableCell>
                        {tasksSummary.totalTasksCompletedOverall}/
                        {tasksSummary.totalTasks}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>All Assigned Tasks</TableCell>
                      <TableCell>{tasksSummary.totalTasksAssigned}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Unassigned Tasks</TableCell>
                      <TableCell>{tasksSummary.totalTasksUnassigned}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Section>
          <Section>
            <Typography variant='h5'>Section 4: Goals View</Typography>
            <Paper sx={{ overflow: 'hidden', marginTop: '20px' }}>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Goal Title</TableCell>
                      <TableCell>Tasks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {goalsPrioritizedByTaskCreateDate.map(
                      (goal: GoalsPrioritizedByTaskCreateDate) => (
                        <TableRow key={goal.goalId}>
                          <TableCell>
                            <a href={goal.goalLink || ''}>{goal.goalTitle}</a>
                          </TableCell>
                          <TableCell>{`${goal.numTasksCompleted}/${goal.numTasks}`}</TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Section>
          <Section>
            <Typography variant='h5'>
              Section 5: Funder Collaborated Goals View
            </Typography>
            <Paper sx={{ overflow: 'hidden', marginTop: '20px' }}>
              <TableContainer sx={{ maxHeight: 300 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Goal Title</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {funderCollaboratedGoals.map(
                      (goal: FunderCollaboratedGoal) => (
                        <TableRow key={goal.goalId}>
                          <TableCell>
                            <a href={goal.goalLink || ''}>{goal.goalTitle}</a>
                            <br />
                            <Typography variant='caption'>
                              Last Updated At{' '}
                              {moment
                                .utc(goal.updatedAt)
                                .format('MM-DD-YYYY HH:mm')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            <Section>
              <Typography variant='h5'>
                <Link
                  href={impactGoalsUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  Impact Goals URL
                </Link>
              </Typography>
            </Section>
          </Section>
        </>
      )}
    </ReportContainer>
  )
}
